/**
 * ResetForm — password reset (step 1: send code, step 2: verify + new password).
 */
import { useState } from "react";
import { KeyRound, Check, XIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  email: string;
  onSendResetCode: () => Promise<void>;
  onVerifyReset: (token: string, newPassword: string) => Promise<void>;
  onBack: () => void;
  loading: boolean;
  error: string | null;
  success: string | null;
}

export function ResetForm({ email, onSendResetCode, onVerifyReset, onBack, loading, error, success }: Props) {
  const [step, setStep] = useState<"send" | "verify">("send");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSendResetCode();
    setStep("verify");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    await onVerifyReset(code, newPassword);
  };

  if (step === "send") {
    return (
      <form onSubmit={handleSend} className="space-y-3">
        <p className="text-sm text-muted-foreground">
          We&apos;ll send a recovery code to <strong className="text-foreground">{email}</strong>
        </p>
        <Button type="submit" disabled={loading} className="w-full gap-2 h-10">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Send recovery code
        </Button>
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-muted-foreground hover:text-foreground font-mono"
        >
          ← Back to sign in
        </button>
        {error && <p className="text-sm text-destructive font-mono" role="alert">{error}</p>}
      </form>
    );
  }

  return (
    <form onSubmit={handleVerify} className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Enter the code sent to <strong className="text-foreground">{email}</strong> and choose a new password.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="reset-code" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Recovery code</Label>
        <Input
          id="reset-code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          autoFocus
          className="h-12 text-center text-lg tracking-[0.5em] font-mono"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="reset-new-pw" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">New password</Label>
        <Input
          id="reset-new-pw"
          type="password"
          required
          autoComplete="new-password"
          minLength={8}
          placeholder="Min. 8 characters"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="h-10"
        />
        {newPassword.length > 0 && (
          <div className="flex items-center gap-3 text-[11px] mt-1">
            <span className={`inline-flex items-center gap-1 ${newPassword.length >= 8 ? "text-emerald-400" : "text-muted-foreground/60"}`}>
              {newPassword.length >= 8 ? <Check className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
              8+ chars
            </span>
            <span className={`inline-flex items-center gap-1 ${/[A-Z]/.test(newPassword) ? "text-emerald-400" : "text-muted-foreground/60"}`}>
              {/[A-Z]/.test(newPassword) ? <Check className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
              Uppercase
            </span>
            <span className={`inline-flex items-center gap-1 ${/[0-9!@#$%^&*]/.test(newPassword) ? "text-emerald-400" : "text-muted-foreground/60"}`}>
              {/[0-9!@#$%^&*]/.test(newPassword) ? <Check className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
              Number/Symbol
            </span>
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="reset-confirm" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Confirm new password</Label>
        <Input
          id="reset-confirm"
          type="password"
          required
          autoComplete="new-password"
          minLength={8}
          placeholder="Re-enter password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="h-10"
        />
        {confirmPassword.length > 0 && (
          <p className={`text-[11px] inline-flex items-center gap-1 mt-0.5 ${
            newPassword === confirmPassword ? "text-emerald-400" : "text-destructive"
          }`}>
            {newPassword === confirmPassword
              ? <><Check className="h-3 w-3" /> Passwords match</>
              : <><XIcon className="h-3 w-3" /> Passwords don't match</>}
          </p>
        )}
      </div>
      <Button
        type="submit"
        disabled={loading || code.length < 6 || (confirmPassword.length > 0 && newPassword !== confirmPassword)}
        className="w-full gap-2 h-10"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
        Reset password
      </Button>
      <button type="button" onClick={() => setStep("send")} className="text-xs text-muted-foreground hover:text-foreground font-mono">
        ← Resend code
      </button>
      {success && <p className="text-sm text-emerald-400 font-mono">{success}</p>}
      {error && <p className="text-sm text-destructive font-mono" role="alert">{error}</p>}
    </form>
  );
}
